import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api/axios.config';
import { notify } from '../utils/methods/notification';

const initialState = {
    isLoading: false,
    allPrintDetails: [],
    PrintDetailsDropdown: [],
    addModal: false
};

// fetch all storage locations
export const fetchAllPrintDetails = createAsyncThunk(
    'bmr/fetchAllPrintDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/print_details');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// add storage location
export const addPrintDetails = createAsyncThunk(
    'bmr/addPrintDetails',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.post('/print_details/create', args);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllPrintDetails());
            return response.data;
        } catch (error) {
            notify.error(error || error.message);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// edit storage location
export const editPrintDetails = createAsyncThunk(
    'bmr/editPrintDetails',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { data, editId } = args;
            const response = await api.put(`/print_details/update/${editId}`, data);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllPrintDetails());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// delete storage location
export const deletePrintDetails = createAsyncThunk(
    'bmr/deletePrintDetails',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { id } = args;
            const response = await api.delete(`/print_details/${id}`);
            notify.success(response.data.message);
            dispatch(fetchAllPrintDetails());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// @info get Esential patient for the portal 1
export const fetchPrintDetailsDropDown = createAsyncThunk(
    'bmr/fetchPrintDetailsDropDown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/print_details/active/status`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

const bmrPrintDetailsSlice = createSlice({
    name: 'bmrPrintDetails',
    initialState,
    reducers: {
        enableAddModal(state) {
            state.addModal = true;
        },
        disableAddModal(state) {
            state.addModal = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch
            .addCase(fetchAllPrintDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAllPrintDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allPrintDetails = action.payload.data;
            })
            .addCase(fetchAllPrintDetails.rejected, (state) => {
                state.isLoading = false;
            })
            // add
            .addCase(addPrintDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addPrintDetails.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(addPrintDetails.rejected, (state) => {
                state.isLoading = false;
            })
            // edit
            .addCase(editPrintDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(editPrintDetails.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(editPrintDetails.rejected, (state) => {
                state.isLoading = false;
            })
            // delete
            .addCase(deletePrintDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deletePrintDetails.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(deletePrintDetails.rejected, (state) => {
                state.isLoading = false;
            })
            // dropdown
            .addCase(fetchPrintDetailsDropDown.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPrintDetailsDropDown.fulfilled, (state, action) => {
                state.isLoading = false;
                state.PrintDetailsDropdown = action.payload.data.items?.map(op => { return { value: op?.id, label: op.name } })
            })
            .addCase(fetchPrintDetailsDropDown.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { disableAddModal, enableAddModal } = bmrPrintDetailsSlice.actions;

export const selectPrintDetailsLoading = (state) => state.bmrPrintDetails.isLoading;
export const selectPrintDetailsAll = (state) => state.bmrPrintDetails.allPrintDetails;
export const selectPrintDetailsDropdown = (state) => state.bmrPrintDetails.PrintDetailsDropdown;
export const selectBmrAddPrintDetailsModal = (state) => state.bmrPrintDetails.addModal;

export default bmrPrintDetailsSlice.reducer;
